/**
 * authService.js
 * All communication with the Node RBAC backend.
 */

import { RBAC_ENDPOINTS } from '../config/api';
import {
  storeAuthData,
  clearAuthData,
  getRefreshToken,
} from '../utils/auth';
import { rbacFetch } from '../utils/apiClient';
import { clearOfflineQueue } from '../utils/offlineQueue';

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function parseResponse(res) {
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || 'Request failed');
  }
  return json.data;
}

// ─── Auth API calls ───────────────────────────────────────────────────────────

/**
 * Login — stores tokens on success, returns user object
 */
export async function login(email, password) {
  const res = await fetch(RBAC_ENDPOINTS.login, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || 'Invalid email or password');
  }

  const { accessToken, refreshToken, user, sessionId } = json.data;
  storeAuthData({ accessToken, refreshToken, user });
  // Store session ID for session tracking (duration calculation on logout)
  if (sessionId) {
    localStorage.setItem('act_session_id', sessionId);
  }
  return user;
}

/**
 * Logout — clears tokens locally and revokes on server
 */
export async function logout(sessionType = 'manual') {
  try {
    const sessionId = localStorage.getItem('act_session_id') || null;
    await rbacFetch(RBAC_ENDPOINTS.logout, {
      method: 'POST',
      body: JSON.stringify({ session_type: sessionType, session_id: sessionId }),
    });
  } catch {
    // Even if server call fails, clear local tokens
  } finally {
    localStorage.removeItem('act_session_id');
    clearAuthData();
    // Explicit logout: remove any unsaved CAR-T Playbook changes held on this
    // device (they contain clinical data). A session expiry is not a logout, so
    // it keeps them for the same user to save after signing back in.
    clearOfflineQueue();
  }
}

/**
 * Session heartbeat — a non-destructive "still here" marker.
 *
 * The engagement report derives session time from the LOGOUT event, so a user
 * who just closes the tab (no logout) contributes no time. This ping records
 * that the session was alive at this moment, letting the report fall back to
 * (last ping − login) when a logout never arrives. It never ends a session, so
 * it is safe with the session_id being shared across tabs.
 *
 * Fire-and-forget: a failed heartbeat must never disrupt the UI.
 */
export async function sendSessionPing() {
  const sessionId = localStorage.getItem('act_session_id') || null;
  if (!sessionId) return;
  try {
    await rbacFetch(RBAC_ENDPOINTS.sessionPing, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  } catch {
    // Non-blocking — the next heartbeat will try again.
  }
}

/**
 * Refresh access token using stored refresh token
 * Returns new user object or null on failure
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(RBAC_ENDPOINTS.refreshToken, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refreshToken }),
    });

    const json = await res.json();
    if (!res.ok) {
      clearAuthData();
      return null;
    }

    const { accessToken, refreshToken: newRefreshToken, user } = json.data;
    storeAuthData({ accessToken, refreshToken: newRefreshToken, user });
    return user;
  } catch {
    clearAuthData();
    return null;
  }
}

/**
 * Get current user profile from server
 */
export async function getMe() {
  const res = await rbacFetch(RBAC_ENDPOINTS.me);
  return parseResponse(res);
}

// ─── User Management API calls (superadmin only) ──────────────────────────────

export async function fetchUsers({ page = 1, limit = 50, search = '', role = '' } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (search) params.set('search', search);
  if (role)   params.set('role', role);

  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}?${params}`);
  return parseResponse(res);
}

export async function fetchUserById(id) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}`);
  return parseResponse(res);
}

export async function createUser(payload) {
  const res = await rbacFetch(RBAC_ENDPOINTS.users, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateUser(id, payload) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}`, {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteUser(id) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}`, {
    method: 'DELETE',
  });
  return parseResponse(res);
}

/**
 * Email the user a fresh single-use link to set their own password.
 * For the cases the automatic retry cannot reach: a corrected address, a
 * message lost to spam, or a link left unused past its 72 hours.
 */
export async function resendAccessLink(id) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}/resend-access-link`, {
    method: 'POST',
  });
  return parseResponse(res);
}

export async function fetchRoles() {
  const res = await rbacFetch(RBAC_ENDPOINTS.roles);
  return parseResponse(res);
}

export async function fetchFilterConfig() {
  const res = await rbacFetch(RBAC_ENDPOINTS.filtersConfig);
  return parseResponse(res);
}

export async function fetchAuditLogs({ page = 1, limit = 50, action = '', user_id = '', date_from = '', date_to = '', exclude_actions = '', include_actions = '', sort_by = '', sort_dir = '', search = '' } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (action)          params.set('action',          action);
  if (user_id)         params.set('user_id',         user_id);
  if (date_from)       params.set('date_from',       date_from);
  if (date_to)         params.set('date_to',         date_to);
  if (exclude_actions) params.set('exclude_actions',  exclude_actions);
  if (include_actions) params.set('include_actions',  include_actions);
  if (sort_by)         params.set('sort_by',          sort_by);
  if (sort_dir)        params.set('sort_dir',         sort_dir);
  if (search)          params.set('search',           search);
  const res = await rbacFetch(`${RBAC_ENDPOINTS.auditLogs}?${params}`);
  return parseResponse(res);
}

/**
 * Every audit entry matching the current filters, not just the first page.
 *
 * The export used to make a single request with limit 10000, which the backend
 * caps at exactly that — so beyond 10,000 matching entries it handed over a
 * truncated file with nothing to say so. An audit export that quietly drops
 * records is the wrong thing to find out about during an audit.
 *
 * @param {object}   filters      same shape as fetchAuditLogs, minus page
 * @param {function} [onProgress] called with (loaded, total) after each page
 */
export async function fetchAllAuditLogs(filters = {}, onProgress) {
  const PAGE_SIZE = 1000;  // well under the backend cap, few enough requests
  const MAX_PAGES = 500;   // hard stop so a runaway loop cannot hang the browser

  const all = [];
  let page = 1;
  let totalPages = 1;
  let total = 0;

  do {
    const data = await fetchAuditLogs({ ...filters, page, limit: PAGE_SIZE });
    all.push(...(data.logs || []));
    totalPages = data.pagination?.totalPages || 1;
    total = data.pagination?.total || all.length;
    if (onProgress) onProgress(all.length, total);
    page += 1;
  } while (page <= totalPages && page <= MAX_PAGES);

  return { logs: all, total };
}

export async function fetchEngagementReport({ page = 1, limit = 50, search = '', sort_by = '', sort_dir = '', role = '', status = '', days_range = 30 } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (search)     params.set('search', search);
  if (sort_by)    params.set('sort_by', sort_by);
  if (sort_dir)   params.set('sort_dir', sort_dir);
  if (role)       params.set('role', role);
  if (status)     params.set('status', status);
  if (days_range) params.set('days_range', days_range);
  const res = await rbacFetch(`${RBAC_ENDPOINTS.engagementReport}?${params}`);
  return parseResponse(res);
}

/**
 * Every user matching the current filters, not just the visible page.
 *
 * The report is paginated, so exporting what is on screen silently produces a
 * file covering 50 of however many users there are. This walks the pages until
 * the whole set is in hand.
 *
 * @param {object}   filters   same shape as fetchEngagementReport, minus page
 * @param {function} [onProgress] called with (loaded, total) after each page
 */
export async function fetchAllEngagementRows(filters = {}, onProgress) {
  const PAGE_SIZE = 100; // the largest the endpoint allows, so fewest requests
  const MAX_PAGES = 200; // hard stop — a runaway loop must never hang the browser

  const all = [];
  let page = 1;
  let totalPages = 1;
  let total = 0;

  do {
    const data = await fetchEngagementReport({ ...filters, page, limit: PAGE_SIZE });
    all.push(...(data.report || []));
    totalPages = data.pagination?.totalPages || 1;
    total = data.pagination?.total || all.length;
    if (onProgress) onProgress(all.length, total);
    page += 1;
  } while (page <= totalPages && page <= MAX_PAGES);

  return { rows: all, total };
}

export async function fetchSyncPreview() {
  const res = await rbacFetch(RBAC_ENDPOINTS.syncPreview);
  return parseResponse(res);
}

export async function importSyncDoctors(doctors) {
  const res = await rbacFetch(RBAC_ENDPOINTS.syncImport, {
    method: 'POST',
    body:   JSON.stringify({ doctors }),
  });
  return parseResponse(res);
}

export async function forgotPassword(email) {
  const res = await fetch(RBAC_ENDPOINTS.forgotPassword, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email }),
  });
  const json = await res.json();
  return json.data;
}

export async function resetPassword(token, password) {
  const res = await fetch(RBAC_ENDPOINTS.resetPassword, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ token, password }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Reset failed');
  return json.data;
}

export async function changePassword(currentPassword, newPassword) {
  const res = await rbacFetch(RBAC_ENDPOINTS.changePassword, {
    method: 'POST',
    body:   JSON.stringify({ currentPassword, newPassword }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to change password');
  return json.data;
}

/**
 * Accept Terms & Conditions — called once on first login
 * Updates terms_accepted in DB and returns success
 */
export async function acceptTerms() {
  const res = await rbacFetch(RBAC_ENDPOINTS.acceptTerms, {
    method: 'POST',
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to accept terms');
  return json.data;
}

// ─── Assignment APIs ──────────────────────────────────────────────────────────

export async function fetchUserAssignments(userId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.userAssignments(userId));
  return parseResponse(res);
}

export async function fetchUserHospitals(userId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.userHospitals(userId));
  return parseResponse(res);
}

export async function updateUserAssignments(userId, payload) {
  const res = await rbacFetch(RBAC_ENDPOINTS.userAssignments(userId), {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function fetchMetaHospitals() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaHospitals);
  return parseResponse(res);
}

export async function fetchMetaZones() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaZones);
  return parseResponse(res);
}

export async function fetchMetaSalesReps() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaSalesReps);
  return parseResponse(res);
}

export async function fetchMetaZBMs() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaZBMs);
  return parseResponse(res);
}
